import v4api, { v4ScopeFor } from "../api/V4client";
import { getStore } from "../configureStore";
import {
  refreshV4Alarms,
  refreshV4ClosedAlarms,
  v4AlarmClosedLive,
  v4AlarmForwardingChanged,
  v4AlarmUpdatedLive,
} from "../redux/actions/alarm.actions";
import {
  reconcileDeviceStatus,
  refreshDeviceLocation,
  resyncV4DeviceStatuses,
  setDeviceAvailability,
  setDeviceOnline,
  syncRunEvent,
} from "../redux/actions/deviceV4.actions";
import { v4GeofencePresenceChanged } from "../redux/actions/geofence.actions";
import { refreshV4ScenarioLastTriggered } from "../redux/actions/scenario.actions";
import { consoleDebug, consoleWarn } from "./log";

// `device.{uuid}.availability.changed.{true|false}`
const AVAILABILITY_TOPIC = /^device\.([^.]+)\.availability\.changed\.(true|false)$/;
// `device.{uuid}.{connected|disconnected}`
const CONNECTION_TOPIC = /^device\.([^.]+)\.(connected|disconnected)$/;
// `geofence.{uuid}.device.{uuid}.{entered|exited}`
const GEOFENCE_PRESENCE_TOPIC = /^geofence\.([^.]+)\.device\.([^.]+)\.(entered|exited)$/;
// Any other `device.{uuid}.…` event.
const DEVICE_TOPIC = /^device\.([^.]+)\./;
// `device.{uuid}.location.{locationId}.created`
const LOCATION_TOPIC = /^device\.([^.]+)\.location\.[^.]+\.created$/;
// `device.{uuid}.sync_run.{runId}.{created|progress|succeeded|failed|cancelled}`
const SYNC_RUN_TOPIC = /^device\.([^.]+)\.sync_run\.([^.]+)\.(created|progress|succeeded|failed|cancelled)$/;
// `arc.alarm.{alarmId}.forwarding.{started|succeeded|failed}` — ARC forwarding lifecycle.
const ARC_FORWARDING_TOPIC = /^arc\.alarm\.([^.]+)\.forwarding\.(started|succeeded|failed)$/;
// `alarm.{alarmId}.{created|closed|updated|...}` — alarms carry an `origin` now, so
// the topic is no longer device-scoped. Matched by prefix, not arity: the form it
// replaces was a substring catch-all, so deeper actions must still reach the refresh.
const ALARM_TOPIC = /^alarm\.[^.]+\./;
// `alarm.{alarmId}.closed` — the alarm itself, not a deeper `….closed` topic.
const ALARM_CLOSED_TOPIC = /^alarm\.([^.]+)\.closed$/;
const ALARM_UPDATED_TOPIC = /^alarm\.([^.]+)\.updated$/;
// `scenario.{scenarioId}.action.{actionId}.triggered`
const SCENARIO_ACTION_TRIGGERED_TOPIC = /^scenario\.([^.]+)\.action\.[^.]+\.triggered$/;
// RTC room lifecycle (exact topic unconfirmed) — any `rtc` / `rtc_…` segment.
const RTC_TOPIC = /(^|\.)rtc([._-]|$)/i;

// Maps a forwarding event action to the arc_forward status the list ring reads.
const FORWARDING_STATUS = {
  started: "forwarding",
  succeeded: "success",
  failed: "failed",
};

const V4_BASE = process.env.REACT_APP_API_V4_BASE || "";
// Derive the WebSocket origin from the V4 HTTP base (https→wss, http→ws).
const V4_WS_BASE = V4_BASE.replace(/^http/i, "ws");

// Sentinel "account id" for V4-native sessions: request the ticket unscoped.
export const V4_NATIVE_ACCOUNT = "v4-native";

const RECONNECT_MIN_MS = 2000;
const RECONNECT_MAX_MS = 60 * 1000;
// How long a socket must stay open before we trust it and reset the backoff —
// stops a connect-then-close server from reconnecting at the minimum delay.
const STABLE_CONNECTION_MS = 10 * 1000;

let instance = null;

/**
 * Single connection per page that pipes V4 alarm events into the store.
 */
export class V4AlarmWebSocket {
  static inst() {
    if (!instance) instance = new V4AlarmWebSocket();
    return instance;
  }

  constructor() {
    this.ws = null;
    this.currentAccountId = null;
    this.connecting = false;
    this.shouldReconnect = false;
    this.reconnectDelay = RECONNECT_MIN_MS;
    this.reconnectTimer = null;
    this.stableTimer = null;
    this.connected = false;
  }

  isConnected() {
    return this.connected && this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Open (or switch) the V4 alarm websocket for the given V3 accountId.
   * If already connected for the same account, no-op. If a different account
   * is currently active, the existing socket is closed first.
   */
  async connectForAccount(v3AccountId) {
    if (!v3AccountId) {
      this.disconnect();
      return;
    }
    // Same account: leave a connection, in-flight connect, OR pending reconnect
    // alone — re-triggers must not bypass the backoff by reopening immediately.
    if (
      this.currentAccountId === v3AccountId &&
      (this.isConnected() || this.connecting || this.reconnectTimer)
    ) {
      return;
    }
    this._teardown();
    this.currentAccountId = v3AccountId;
    this.shouldReconnect = true;
    this.reconnectDelay = RECONNECT_MIN_MS;
    await this._open();
  }

  disconnect() {
    this.shouldReconnect = false;
    this.currentAccountId = null;
    this._teardown();
  }

  _clearStableTimer() {
    if (this.stableTimer) {
      clearTimeout(this.stableTimer);
      this.stableTimer = null;
    }
  }

  _teardown() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this._clearStableTimer();
    if (this.ws) {
      try {
        // Clear handlers first so the upcoming close doesn't trigger a
        // reconnect for the account we're tearing down.
        this.ws.onopen = null;
        this.ws.onmessage = null;
        this.ws.onerror = null;
        this.ws.onclose = null;
        this.ws.close();
      } catch (_) {}
    }
    this.ws = null;
    this.connecting = false;
    this.connected = false;
  }

  async _open() {
    if (!V4_WS_BASE) {
      consoleWarn("[v4-ws] REACT_APP_API_V4_BASE not set — skipping");
      return;
    }
    if (!this.currentAccountId || this.connecting) return;
    this.connecting = true;
    const accountId = this.currentAccountId;
    let ticket;
    try {
      // V4-native: the token's own account is the scope.
      const scope =
        accountId === V4_NATIVE_ACCOUNT ? undefined : await v4ScopeFor(accountId);
      const res = await v4api.websocketTicketList(scope);
      ticket = res?.data?.ticket;
      if (!ticket) throw new Error("no ticket in response");
    } catch (e) {
      consoleWarn("[v4-ws] failed to fetch ticket", e);
      this.connecting = false;
      if (this.currentAccountId === accountId) this._scheduleReconnect();
      return;
    }
    // Account may have switched while we were fetching the ticket — abort.
    if (this.currentAccountId !== accountId) {
      this.connecting = false;
      return;
    }
    const url = `${V4_WS_BASE}/v1/streams/websocket/${encodeURIComponent(ticket)}`;
    consoleDebug(`[v4-ws] connecting (account ${accountId})`);
    let socket;
    try {
      socket = new WebSocket(url);
    } catch (e) {
      consoleWarn("[v4-ws] WebSocket constructor failed", e);
      this.connecting = false;
      this._scheduleReconnect();
      return;
    }
    this.ws = socket;
    socket.onopen = () => {
      if (this.ws !== socket) return;
      consoleDebug("[v4-ws] connected");
      this.connecting = false;
      this.connected = true;
      // Resync now that we're subscribed: any alarm/forwarding state that changed
      // during the connect handshake produced no delivered event, so a fresh
      // fetch is needed to close the fetch-vs-subscription gap.
      try {
        getStore().dispatch(refreshV4Alarms(this.currentAccountId));
        // Same gap for device statuses.
        getStore().dispatch(resyncV4DeviceStatuses(this.currentAccountId));
      } catch (e) {
        consoleWarn("[v4-ws] failed to dispatch resync on connect", e);
      }
      // Reset the backoff only once the socket has stayed open long enough to
      // be trusted, not on open alone — see STABLE_CONNECTION_MS.
      this._clearStableTimer();
      this.stableTimer = setTimeout(() => {
        this.stableTimer = null;
        if (this.ws === socket && this.connected) {
          consoleDebug("[v4-ws] connection stable — backoff reset");
          this.reconnectDelay = RECONNECT_MIN_MS;
        }
      }, STABLE_CONNECTION_MS);
    };
    socket.onmessage = (msg) => {
      if (this.ws !== socket) return;
      this._onMessage(msg);
    };
    socket.onerror = (e) => {
      if (this.ws !== socket) return;
      consoleWarn("[v4-ws] socket error", e?.message || e);
    };
    socket.onclose = (e) => {
      if (this.ws !== socket) return;
      consoleDebug(`[v4-ws] closed (${e?.code}) ${e?.reason || ""}`);
      this._clearStableTimer();
      this.ws = null;
      this.connecting = false;
      this.connected = false;
      if (this.shouldReconnect && this.currentAccountId) this._scheduleReconnect();
    };
  }

  // A sign of life: the saga re-reads REST if the device is shown offline.
  _deviceSeen(uuid) {
    getStore().dispatch(
      reconcileDeviceStatus(uuid, undefined, this.currentAccountId)
    );
  }

  _onMessage(msg) {
    let parsed;
    try {
      parsed = JSON.parse(msg.data);
    } catch (_) {
      // Non-JSON frames (e.g. server heartbeats) are ignored.
      return;
    }
    if (parsed?.type !== "event") return;
    const topic = parsed?.data?.topic;
    if (typeof topic !== "string") return;

    // Update availability-gated UI (e.g. the OSM button) without a refresh.
    const availability = topic.match(AVAILABILITY_TOPIC);
    if (availability) {
      const [, uuid, value] = availability;
      consoleDebug(`[v4-ws] availability event: ${uuid} -> ${value}`);
      try {
        getStore().dispatch(setDeviceAvailability(uuid, value === "true"));
        if (value === "true") this._deviceSeen(uuid);
      } catch (e) {
        consoleWarn("[v4-ws] failed to dispatch availability", e);
      }
      return;
    }

    const connection = topic.match(CONNECTION_TOPIC);
    if (connection) {
      const [, uuid, action] = connection;
      consoleDebug(`[v4-ws] connection event: ${uuid} -> ${action}`);
      const online = action === "connected";
      try {
        // Shown at once (arrival time stands in for the connect/disconnect
        // time), then settled against REST.
        getStore().dispatch(
          setDeviceOnline(uuid, online, Math.floor(Date.now() / 1000))
        );
        getStore().dispatch(
          reconcileDeviceStatus(uuid, online, this.currentAccountId)
        );
      } catch (e) {
        consoleWarn("[v4-ws] failed to dispatch connection", e);
      }
      return;
    }

    // Live geofence presence — flip the member's present flag without a refetch.
    const presence = topic.match(GEOFENCE_PRESENCE_TOPIC);
    if (presence) {
      const [, geofenceId, deviceId, action] = presence;
      consoleDebug(`[v4-ws] geofence presence event: ${deviceId} ${action} ${geofenceId}`);
      try {
        getStore().dispatch(
          v4GeofencePresenceChanged(geofenceId, deviceId, action === "entered")
        );
      } catch (e) {
        consoleWarn("[v4-ws] failed to dispatch geofence presence", e);
      }
      return;
    }

    // Live location — a new fix was created; pull it and move the device on the
    // map without a manual refresh. The event carries only the device uuid.
    const location = topic.match(LOCATION_TOPIC);
    if (location) {
      const [, uuid] = location;
      consoleDebug(`[v4-ws] location event: ${uuid}`);
      try {
        getStore().dispatch(refreshDeviceLocation(uuid, this.currentAccountId));
        this._deviceSeen(uuid);
      } catch (e) {
        consoleWarn("[v4-ws] failed to dispatch location refresh", e);
      }
      return;
    }

    // Device config sync run state change — resolves a saga waiting on the run
    const syncRun = topic.match(SYNC_RUN_TOPIC);
    if (syncRun) {
      const [, uuid, runId, status] = syncRun;
      consoleDebug(`[v4-ws] sync_run event: ${uuid} ${runId} -> ${status}`);
      try {
        getStore().dispatch(syncRunEvent(uuid, runId, status));
      } catch (e) {
        consoleWarn("[v4-ws] failed to dispatch sync_run event", e);
      }
      return;
    }

    // ARC forwarding lifecycle — update the status in place so the ring reacts
    // instantly. The reducer keeps that live status over any refetch the read
    // model hasn't caught up with, so it goes first. Other arc_forward fields
    // reconcile via the refetch below.
    const forwarding = topic.match(ARC_FORWARDING_TOPIC);
    if (forwarding) {
      const [, alarmId, action] = forwarding;
      consoleDebug(`[v4-ws] arc forwarding event: ${alarmId} -> ${action}`);
      try {
        getStore().dispatch(
          v4AlarmForwardingChanged(alarmId, FORWARDING_STATUS[action])
        );
        // A forward that starts is a new alarm for the ARC it goes to, and that
        // console hears of no `alarm.{id}.created` — refetch so it shows up. The
        // settling events refetch too, in case the first one beat the read model.
        getStore().dispatch(refreshV4Alarms(this.currentAccountId));
      } catch (e) {
        consoleWarn("[v4-ws] failed to dispatch arc forwarding event", e);
      }
      return;
    }

    const triggeredScenario = topic.match(SCENARIO_ACTION_TRIGGERED_TOPIC);
    if (triggeredScenario) {
      const [, scenarioId] = triggeredScenario;
      consoleDebug(`[v4-ws] scenario action triggered: ${scenarioId}`);
      try {
        getStore().dispatch(
          refreshV4ScenarioLastTriggered(scenarioId, this.currentAccountId)
        );
      } catch (e) {
        consoleWarn("[v4-ws] failed to dispatch scenario refresh", e);
      }
      return;
    }

    // The room lands after the `alarm.{id}.created` refetch; refetch on its own event.
    const device = topic.match(DEVICE_TOPIC);
    if (RTC_TOPIC.test(topic)) {
      consoleDebug(`[v4-ws] rtc event: ${topic}`);
      try {
        getStore().dispatch(refreshV4Alarms(this.currentAccountId));
        if (device) this._deviceSeen(device[1]);
      } catch (e) {
        consoleWarn("[v4-ws] failed to dispatch rtc refresh", e);
      }
      return;
    }

    // Unknown device event — logged, in case it is a connection topic of another shape.
    if (device && topic.indexOf(".alarm.") === -1) {
      consoleDebug(`[v4-ws] unhandled device event: ${topic}`);
      try {
        this._deviceSeen(device[1]);
      } catch (e) {
        consoleWarn("[v4-ws] failed to dispatch device status check", e);
      }
      return;
    }

    // Alarm lifecycle: `alarm.{alarmId}.{action}`, or the legacy device-scoped
    // `device.{uuid}.alarm.{type}.{alarmId}.{action}`. ARC forwarding is alarm-scoped
    // too, but returned above with its own optimistic update. An alarm centre
    // refetches its ARC's list here (alarmV4.saga picks it), so a close elsewhere
    // takes the alarm off the console.
    if (!ALARM_TOPIC.test(topic) && topic.indexOf(".alarm.") === -1) {
      consoleDebug(`[v4-ws] unhandled event: ${topic}`);
      return;
    }
    consoleDebug(`[v4-ws] alarm event: ${topic}`);
    try {
      // Closed at once: the refetch may lag the event.
      const closed = topic.match(ALARM_CLOSED_TOPIC);
      if (closed) getStore().dispatch(v4AlarmClosedLive(closed[1]));
      const updated = topic.match(ALARM_UPDATED_TOPIC);
      if (updated) getStore().dispatch(v4AlarmUpdatedLive(updated[1], this.currentAccountId));
      getStore().dispatch(refreshV4Alarms(this.currentAccountId));
      // A close moves the alarm into the closed list — refresh that too (action is
      // the trailing topic segment; no-op unless the closed list is loaded).
      if (topic.split(".").pop() === "closed") {
        getStore().dispatch(refreshV4ClosedAlarms(this.currentAccountId));
      }
    } catch (e) {
      consoleWarn("[v4-ws] failed to dispatch refresh", e);
    }
  }

  _scheduleReconnect() {
    if (!this.shouldReconnect || !this.currentAccountId) return;
    if (this.reconnectTimer) return;
    const delay = this.reconnectDelay;
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, RECONNECT_MAX_MS);
    consoleDebug(`[v4-ws] reconnect in ${delay}ms`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this._open();
    }, delay);
  }
}
